// DS: MOF3.1
import React, { useContext, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import noop from '@wix/da-shared-react/pkg/utils/noop';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import TagInput from '@wix/da-ds/pkg/formControls/dsPresets/TagInput';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { type SubNavigation } from '../../../../../types/store';
import { type ShopFilters } from '../../../../../types/shop';
import ShopFiltersPriceRadioGroup from './ShopFiltersPriceRadioGroup';
import ShopFiltersSaleTypeRadioGroup from './ShopFiltersSaleTypeRadioGroup';

import s from './ShopFiltersForm.scss';

export interface Props {
  searchPageType?: string;
  className?: string;
  filters?: any;
  priceRanges?: SubNavigation;
  saleTypes?: SubNavigation;
  status?: SubNavigation;
  updateFilters?: (update?: ShopFilters) => void;
  onApply?: (update?: ShopFilters) => void;
  resetFilters?: () => void;
}

type SaleType = ShopFilters['sale_type'];

const EXCLUSIVES_PAGE_TYPES = ['exclusives', 'adoptables'];

const ShopFiltersForm: React.FC<Props> = ({
  searchPageType,
  className,
  filters = {},
  priceRanges,
  saleTypes,
  updateFilters = noop,
  onApply = noop,
  resetFilters = noop,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [tag, setTag] = useState('');

  const isForDownloads = searchPageType === 'downloads';
  const isForSubscriptions = searchPageType === 'subscriptions';
  const isForExclusives =
    !!searchPageType && EXCLUSIVES_PAGE_TYPES.includes(searchPageType);
  const hasTagInput = isForDownloads || isForSubscriptions || isForExclusives;

  return (
    <form
      className={classnames(className, s['root'])}
      onSubmit={ev => ev.preventDefault()}
      onReset={ev => {
        ev.preventDefault();
        resetFilters();
      }}
    >
      {hasTagInput && (
        // it gets complicated because this page only allows one tag at a time
        <TagInput
          className={s['tag-input']}
          noBottomSpacing
          id="shop-filters-tag"
          label={t(`shopPage.shopFiltersForm.tag.label`)}
          placeholder={t('shopPage.shopFiltersForm.tag.placeholder')}
          value={tag}
          onChange={ev => setTag(ev.target.value.trim())}
          tags={filters.tag ? [filters.tag] : []}
          tagSuggestions={[]}
          onAddTag={() => {
            updateFilters({ tag });
            setTag('');
          }}
          onRemoveTag={() => updateFilters({ tag: '' })}
          onCancel={() => setTag('')}
          onKeyUp={e => {
            if (e.code === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              updateFilters({ tag });
              setTag('');
            }
          }}
        />
      )}

      <FilterToggle
        name="watched"
        value={filters.watched}
        updater={updateFilters}
      />

      {isForExclusives && (
        <FilterToggle
          name="resale_allowed"
          value={filters.resale_allowed}
          updater={updateFilters}
        />
      )}

      {isForDownloads && (
        <FilterToggle name="dd" value={filters.dd} updater={updateFilters} />
      )}

      {isForSubscriptions && (
        <FilterToggle
          name="discord"
          value={filters.discord}
          updater={updateFilters}
        />
      )}

      {saleTypes && (
        <ShopFiltersSaleTypeRadioGroup
          saleTypes={saleTypes}
          onChange={(_, value: SaleType) => updateFilters({ sale_type: value })}
          currentFilterValue={filters.sale_type}
          className={s['sale-types']}
        />
      )}

      {!!priceRanges && (
        <ShopFiltersPriceRadioGroup
          className={s['price-ranges']}
          rangeClassName={s['price-range']}
          priceRanges={priceRanges}
          minPrice={filters.min_price}
          maxPrice={filters.max_price}
          setRange={(minPrice?: number, maxPrice?: number) =>
            updateFilters({ min_price: minPrice, max_price: maxPrice })
          }
        />
      )}

      {isMobile ? (
        <div className={s['mobile-button-group']}>
          <GhostButton
            type="reset"
            variant="main"
            size="large"
            onClick={resetFilters}
          >
            {t('shopPage.shopFiltersForm.resetButton.mobile')}
          </GhostButton>

          <SolidButton
            type="button"
            variant="approval-green"
            size="large"
            priority="primary"
            onClick={() => {
              // the user might press 'apply' when the tag is local to this form only
              // and hasn't been set in filters
              onApply(tag ? { tag } : undefined);
              setTag('');
            }}
          >
            {t('shopPage.shopFiltersForm.applyButton.mobile')}
          </SolidButton>
        </div>
      ) : (
        <TextButton
          className={s['reset-button-desktop']}
          type="reset"
          variant="main"
          size="small"
          onClick={resetFilters}
        >
          {t('shopPage.shopFiltersForm.resetButton')}
        </TextButton>
      )}
    </form>
  );
};

ShopFiltersForm.displayName = 'ShopFiltersForm';
export default ShopFiltersForm;

// break this out to decrease boilerplate
function FilterToggle({
  name,
  value,
  updater,
}: {
  name: string;
  value: any;
  updater: (update?: ShopFilters) => void;
}) {
  const { t } = useTranslation();

  const id = `shop-filters-${name}`;
  const labelId = `${id}-label`;
  return (
    <label className={s['toggle-label']} id={labelId} htmlFor={id}>
      {t(`shopPage.shopFiltersForm.${name}.label`)}
      <MainToggle
        variant="main"
        className={s['toggle']}
        id={id}
        name={name}
        aria-labelledby={labelId}
        size="small"
        value={value}
        type="button"
        onChange={newValue => updater({ [name]: newValue })}
      />
    </label>
  );
}
